import { CourierSettlementsApiAdapter } from '../../adapters';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/useToast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface UseRecalculateCourierSettlementPendingLinesParams {
  settlementId: number;
  courierInvoiceId?: number | null;
}

export function useRecalculateCourierSettlementPendingLines({
  settlementId,
  courierInvoiceId,
}: UseRecalculateCourierSettlementPendingLinesParams) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('courierInvoicesModule');
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new CourierSettlementsApiAdapter(httpClient), [httpClient]);

  return useMutation({
    mutationFn: () => adapter.recalculatePendingLines(settlementId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['courier-settlement-detail', settlementId] });
      void queryClient.invalidateQueries({ queryKey: ['courier-settlement-lines', settlementId] });
      void queryClient.invalidateQueries({ queryKey: ['courier-settlement-exceptions', settlementId] });
      if (courierInvoiceId != null && courierInvoiceId > 0) {
        void queryClient.invalidateQueries({ queryKey: ['courier-invoice-detail', courierInvoiceId] });
      }
      toast({
        title: t('settlement.recalculate_pending.success_title'),
        description: t('settlement.recalculate_pending.success_description', {
          recalculated: data.recalculated,
          lines_matched_count: data.lines_matched_count,
        }),
      });
    },
    onError: (err: unknown) => {
      const e = err as { statusCode?: number; message?: string };
      const is422 = e?.statusCode === 422;
      toast({
        title: is422
          ? t('settlement.recalculate_pending.error_422_title')
          : t('settlement.recalculate_pending.error_title'),
        description:
          (e?.message && String(e.message).trim()) ||
          (is422 ? t('settlement.recalculate_pending.error_422_hint') : t('errors.unexpected')),
        variant: 'destructive',
      });
    },
  });
}
